import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Home, Bed, Bath, Wifi, Wind, Utensils, Car, Trees, Umbrella, ShowerHead, Warehouse } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const amenityIcons = {
  'WiFi': Wifi,
  'Aria Condizionata': Wind,
  'Cucina Attrezzata': Utensils,
  'Parcheggio': Car,
  'Parcheggio Coperto': Warehouse,
  'Giardino Privato': Trees,
  'Pergola con Zanzariera': Umbrella,
  'Pergola Laterale': Umbrella,
  'Pergola Esterna': Umbrella,
  'Doccia Esterna': ShowerHead,
  'Veranda': Home,
  'Lavanderia': Home,
  'Nuova Costruzione': Home
};

const PropertiesPage = () => {
  const { t } = useLanguage();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProperties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Errore nel caricamento degli appartamenti');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <p className="text-[#666666]">{t('properties.loading')}</p>
      </div>
    );
  }

  return (
    <div data-testid="properties-page" className="min-h-screen pt-20">
      <section className="bg-[#F9F6F0] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-2">
            {t('properties.overline')}
          </p>
          <h1
            data-testid="properties-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight text-[#1A1A1A] mb-4"
          >
            {t('properties.title')}
          </h1>
          <p className="text-base text-[#666666] max-w-2xl mx-auto leading-relaxed">
            {t('properties.subtitle')}
          </p>
        </div>
      </section>

      {/* Properties Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {properties.map((property, index) => (
              <motion.div
                key={property.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`property-card-${property.id}`}
                className="bg-white border border-[#2C5263]/10 rounded-lg overflow-hidden hover:-translate-y-1 transition-transform"
              >
                {/* Image */}
                <div className="aspect-video bg-[#F9F6F0] overflow-hidden relative group">
                  <img
                    src={property.images?.[0] || "https://images.unsplash.com/photo-1757924461488-ef9ad0670978?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGVsZWdhbnR8ZW58MHx8fHwxNzg0NjI1NDg0fDA&ixlib=rb-4.1.0&q=85"}
                    alt={property.name}
                    className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${property.status === 'wip' ? 'grayscale-[30%]' : ''}`}
                  />
                  {property.status === 'wip' ? (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/40 to-transparent" />
                      <div
                        data-testid={`wip-badge-${property.id}`}
                        className="absolute top-4 left-4 bg-[#1A1A1A]/90 backdrop-blur text-white px-3 py-1.5 rounded-md text-xs font-medium tracking-wide uppercase flex items-center gap-1.5 border border-[#D4AF37]/40"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
                        {t('properties.wipBadge')}
                      </div>
                      <div className="absolute top-4 right-4 bg-[#D4AF37] text-white px-3 py-1 rounded-md text-sm font-medium">
                        {t('properties.wipCardLabel')}
                      </div>
                    </>
                  ) : (
                    <div className="absolute top-4 right-4 bg-[#D4AF37] text-white px-3 py-1 rounded-md text-sm font-medium">
                      {t('properties.priceFrom')} €{property.price_low_season}{t('properties.perNight')}
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-serif text-[#1A1A1A] mb-2">
                    {property.name}
                  </h3>
                  <p className="text-sm text-[#666666] mb-4 leading-relaxed line-clamp-2">
                    {property.description}
                  </p>

                  <div className="flex flex-wrap gap-4 mb-4 text-sm text-[#666666]">
                    <div className="flex items-center gap-1">
                      <Users size={16} />
                      <span>{property.max_guests} {t('properties.guests')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Bed size={16} />
                      <span>{property.bedrooms} {t('properties.bedrooms')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Bath size={16} />
                      <span>{property.bathrooms} {t('properties.bathrooms')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Home size={16} />
                      <span>{property.size_sqm} m²</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {property.amenities.slice(0, 4).map((amenity) => {
                      const Icon = amenityIcons[amenity];
                      return (
                        <div
                          key={amenity}
                          className="flex items-center gap-1 text-xs text-[#2C5263] bg-[#F9F6F0] px-2 py-1 rounded"
                        >
                          {Icon && <Icon size={14} />}
                          <span>{amenity}</span>
                        </div>
                      );
                    })}
                  </div>

                  {property.status === 'wip' ? (
                    <div className="mb-4 p-3 bg-[#F9F6F0] rounded-lg border border-[#D4AF37]/30">
                      <p className="text-xs text-[#2C5263] font-semibold uppercase tracking-wide mb-1">
                        {t('properties.wipReadyFrom')}
                      </p>
                      <p className="text-xs text-[#666666] leading-relaxed">
                        {t('properties.wipDetailText')}
                      </p>
                    </div>
                  ) : (
                    <div className="mb-4 p-3 bg-[#F9F6F0] rounded-lg">
                      <div className="flex justify-between text-xs text-[#666666] mb-1">
                        <span>{t('properties.lowSeason')}</span>
                        <span className="font-medium">€{property.price_low_season}{t('properties.perNight')}</span>
                      </div>
                      <div className="flex justify-between text-xs text-[#666666] mb-1">
                        <span>{t('properties.midSeason')}</span>
                        <span className="font-medium">€{property.price_mid_season}{t('properties.perNight')}</span>
                      </div>
                      <div className="flex justify-between text-xs text-[#666666]">
                        <span>{t('properties.highSeason')}</span>
                        <span className="font-medium">€{property.price_high_season}{t('properties.perNight')}</span>
                      </div>
                    </div>
                  )}

                  <Link
                    to={`/appartamenti/${property.id}`}
                    data-testid={`view-property-${property.id}`}
                    className="block w-full bg-[#2C5263] text-white text-center px-6 py-3 rounded-md text-sm font-medium hover:bg-[#1A3644] transition-colors"
                  >
                    {t('properties.viewDetails')}
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PropertiesPage;
